import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { DepositBranch as DepositBranchData, WaiverGround } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Badge, Button, Card, CardBody, Dialog, Spinner } from '@fluxora/ui';

import { CardOnFileForm } from './CardOnFileForm';
import { isNotImplemented } from '../api/bff-client';
import { useDepositIntentMutation, useWaiverElectionMutation } from '../api/deposit.queries';
import { useUsageSelection } from '../hooks/useUsageSelection';
import { formatMoney } from '../services/credit.service';
import { buildPlanStepPath } from '../services/funnel-rehydrate.service';
import { applicableWaiverGrounds } from '../services/waiver.service';
import { WaiverMatrix } from './WaiverMatrix';

/**
 * The deposit screen (E-SF05 AC1 + AC2). Reframed 2026-07-22 to a PAYMENT-METHOD
 * SETUP + DEFERRED-DEPOSIT model: the customer either sets up a payment method on
 * file (no lump-sum charge at signup — the PUCT §25.478 deposit is spread across
 * the first bills via the installment schedule) OR elects a §25.478 waiver. One
 * of the two must happen before enrollment proceeds (funnel DEPOSIT_PENDING).
 *
 * ── NO UP-FRONT CHARGE ─────────────────────────────────────────────────────
 * "Set up your payment method" asks the BFF for a deposit intent; the synthetic
 * gateway (dev) puts a method on file and schedules the deposit as installments
 * rather than taking a lump sum. With Stripe test-mode keys the browser tokenises
 * the method via Elements (no card data touches our servers). Nothing is charged
 * today; the deposit rides the customer's first bills.
 */

export interface DepositBranchProps {
  readonly candidateId: string;
  readonly deposit: DepositBranchData;
  /** See `CreditDecisionResultProps.customerAgeYears`. Drives the age-waiver offer. */
  readonly customerAgeYears?: number;
}

export function DepositBranch({
  candidateId,
  deposit,
  customerAgeYears,
}: DepositBranchProps): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const intent = useDepositIntentMutation();
  const waiver = useWaiverElectionMutation();

  // Once a §25.478 waiver is on file the deposit is SETTLED (funnelState
  // DEPOSIT_WAIVED / DEPOSIT_INSTALLMENT_SCHEDULED) and enrollment may proceed —
  // so the deposit branch, like the approve/waived branches, must offer the
  // forward link to the estimate. Without it a waiver-electing customer is
  // stranded on the credit page with no way to continue.
  //
  // ⚠️ BUILT, NOT ASSEMBLED (QA-2026-08-06) — see the same note on
  // `CreditDecisionResult`. Hand-assembling this path was how the estimate step
  // ended up reached with `?candidate=` alone, losing the shopper's chosen kWh.
  const selection = useUsageSelection();
  const toEstimate =
    slug === undefined
      ? '/'
      : buildPlanStepPath({ tariffId: slug, step: 'estimate', selection, candidateId });

  const navigate = useNavigate();
  const [selectedWaiver, setSelectedWaiver] = useState<WaiverGround | undefined>(undefined);
  const [certificationRef, setCertificationRef] = useState('');
  /* Card saved → the waiver question is answered; a popup offers the bill. */
  const [cardSaved, setCardSaved] = useState(false);
  const [savedDialogOpen, setSavedDialogOpen] = useState(false);

  // The dev catalogue's figure is a placeholder either way; the inline
  // "Estimate — not a final charge" badge on the amount is the disclosure
  // (the full amber explainer panel was retired — it dominated the page).
  const amountIsProvisional = true;

  const submitWaiver = (): void => {
    if (selectedWaiver === undefined) return;
    const ref = certificationRef.trim();
    waiver.mutate({
      candidateId,
      election: {
        ground: selectedWaiver,
        acknowledged: true,
        ...(ref.length > 0 ? { certificationRef: ref } : {}),
      },
    });
  };

  return (
    // `max-w-3xl` + `self-start`: the page column is `max-w-6xl`, and stretching
    // this notice across all of it ran the deposit sentence to ~1400px — far
    // past a comfortable measure, and it read as a page-wide banner rather than
    // a card. Capped at a reading width and pinned to the left of the column
    // (2026-09-18 request); `self-start` is explicit so it stays put if a parent
    // ever centres its items.
    // `w-full max-w-3xl`, no `self-start`: FILL the column this sits in, up to a
    // reading width. `self-start` sized the panels to their CONTENT instead of
    // the column, which is what left them looking pinned to the left edge. The
    // cap still applies on the sign step, whose rail is wider.
    <div className="flex w-full max-w-3xl flex-col gap-5">
      {/* ⚠️ A `Card`, NOT an `Alert` — 2026-09-18.
          This notice used to be `<Alert>`, whose container is `px-4 py-3`
          while the two panels below it are `<Card padding="md">` (`p-5`). So
          its heading and body started 4px left of, and its rounded corners
          were tighter than, everything under it — three stacked panels, none
          of whose text lined up. `Alert`'s padding could not be overridden
          from here either: `cn` is plain clsx with no tailwind-merge, so
          `px-4` and an overriding `px-5` would BOTH be emitted and the winner
          decided by stylesheet order, which is not something a layout should
          rest on. Using the same container the siblings use makes them line up
          by construction.
          `role="status"` is carried over deliberately — this panel announces a
          §25.478 deposit outcome and must stay announced. */}
      <Card variant="glass" padding="md" role="status">
        {/* `text-sm` is carried over from `Alert`, which set it on the
            container; `Card` does not, and without it this copy jumps a size
            against the panels below. */}
        <CardBody className="flex flex-col gap-2 p-0 text-sm">
          {/* TOP-LEFT, above the heading (2026-09-18 request). The amount
              carries its own marker so the figure is never readable as final
              even if the notice below scrolls out of view — which is why it
              leads the panel rather than sitting between the heading and the
              body where it read as part of the sentence. */}
          {/* `-ml-2` cancels the pill's own `px-2` so the badge's TEXT lines up
              with the heading and body beneath it, rather than its background.
              Aligning the box left the words visibly indented against every
              other line in the panel — the pill overhangs by 8px now, which is
              the usual trade for optical alignment of a lozenge over copy. */}
          {amountIsProvisional ? (
            <Badge variant="warning" className="-ml-2 self-start">
              {t('shop.deposit.provisional.badge')}
            </Badge>
          ) : null}
          <p className="font-medium">{t('shop.deposit.required.title')}</p>
          <span>{t('shop.deposit.required.body', { amount: formatMoney(deposit.amount) })}</span>
          {deposit.installmentOption !== undefined ? (
            <span className="text-xs">
              {t('shop.deposit.deferred.schedule', {
                amount: formatMoney(deposit.amount),
                first: deposit.installmentOption.firstInstalmentPercent,
                remainderDays: deposit.installmentOption.remainderDueDays,
              })}
            </span>
          ) : null}
        </CardBody>
      </Card>

      {/* ── Set up your payment method (deposit deferred to installments) ──
          Hidden once a WAIVER is chosen: the §25.478 election stands in place
          of the deposit, so demanding a card as well made the waiver look
          fake. Clearing the selection brings the card path back. */}
      {selectedWaiver === undefined && !waiver.isSuccess ? (
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-3 p-0">
          <h3 className="text-sm font-medium text-foreground">{t('shop.deposit.pay.title')}</h3>
          <p className="text-xs text-foreground/70">{t('shop.deposit.pay.saqa')}</p>

          {/* The intent CTA is shown ONLY before setup begins. Once the intent
              succeeds it is replaced by the card form (or the stub note / Stripe
              slot) below — never both a "Set up" and a "Save" button at once. */}
          {!intent.isSuccess ? (
            <Button
              variant="primary"
              onClick={() => intent.mutate({ candidateId })}
              disabled={intent.isPending}
            >
              {intent.isPending ? t('shop.deposit.pay.starting') : t('shop.deposit.pay.cta')}
            </Button>
          ) : null}

          {intent.isPending ? <Spinner size="sm" label={t('shop.deposit.pay.starting')} /> : null}
          {intent.isError ? (
            <Alert variant="danger" role="alert">
              {t('shop.deposit.pay.error')}
            </Alert>
          ) : null}

          {intent.isSuccess && intent.data.mode === 'stub' ? (
            // No-Stripe masked "card on file": the customer enters a card, the browser
            // derives {brand,last4,expMonth,expYear} and sends ONLY that (the full PAN
            // + CVV never leave the browser), the BFF stores the masked descriptor, and
            // it rides onto the account at sign so the CRM shows the card. Works in the
            // deployed `stub` gateway mode (where /bff/deposit/confirm 501s).
            <CardOnFileForm
              candidateId={candidateId}
              amount={deposit.amount}
              amountIsProvisional={amountIsProvisional}
              toEstimate={toEstimate}
              onSaved={() => {
                setCardSaved(true);
                setSavedDialogOpen(true);
              }}
            />
          ) : null}

          {intent.isSuccess && intent.data.mode === 'stripe_test' ? (
            // With test-mode keys, Stripe Elements mounts here (browser→Stripe).
            // Elements is not bundled in this slice (no @stripe dependency), so the
            // slot is a labelled placeholder rather than a fake card field.
            <Alert variant="info" role="status">
              {t('shop.deposit.pay.elementsSlot')}
            </Alert>
          ) : null}
        </CardBody>
      </Card>
      ) : null}

      {/* ── Or elect a §25.478 waiver (AC1) ──────────────────────────────
          Hidden once a CARD is on file: the deposit is settled by the
          installment schedule at that point, and offering hardship waivers
          AFTER payment details reads as a second, contradictory ask. */}
      {!cardSaved ? (
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-4 p-0">
          <WaiverMatrix
            // The age ground is dropped when we can PROVE the customer is under 62.
            // The other four rest on third-party documents we cannot see, so they
            // stay — see waiver.service.ts for why that is deliberate.
            offeredWaivers={applicableWaiverGrounds(deposit.offeredWaivers, customerAgeYears)}
            selected={selectedWaiver}
            onSelect={setSelectedWaiver}
            certificationRef={certificationRef}
            onCertificationRefChange={setCertificationRef}
          />

          {selectedWaiver === 'LOW_INCOME_INSTALLMENT' && deposit.installmentOption !== undefined ? (
            <Alert variant="info" role="status">
              {t('shop.deposit.waiver.installment.note', {
                first: deposit.installmentOption.firstInstalmentPercent,
                firstDays: deposit.installmentOption.firstInstalmentDueDays,
                remainderDays: deposit.installmentOption.remainderDueDays,
              })}
            </Alert>
          ) : null}

          <Button
            variant="secondary"
            onClick={submitWaiver}
            disabled={selectedWaiver === undefined || waiver.isPending}
          >
            {waiver.isPending ? t('shop.deposit.waiver.submitting') : t('shop.deposit.waiver.submit')}
          </Button>

          {waiver.isError ? (
            <Alert
              variant={isNotImplemented(waiver.error) ? 'warning' : 'danger'}
              role={isNotImplemented(waiver.error) ? 'status' : 'alert'}
            >
              {isNotImplemented(waiver.error)
                ? t('shop.deposit.waiver.stub.unavailable')
                : t('shop.deposit.waiver.error')}
            </Alert>
          ) : null}

          {waiver.isSuccess ? (
            <>
              <Alert variant="success" title={t('shop.deposit.waiver.recorded.title')} role="status">
                {waiver.data.funnelState === 'DEPOSIT_INSTALLMENT_SCHEDULED'
                  ? t('shop.deposit.waiver.recorded.installment')
                  : t('shop.deposit.waiver.recorded.waived')}
              </Alert>
              <div className="flex justify-center">
                <Link to={toEstimate}>
                  <Button variant="primary" shape="pill" className="cta-sheen">
                    {t('shop.credit.continueToEstimate')}
                  </Button>
                </Link>
              </div>
            </>
          ) : null}

          {selectedWaiver !== undefined && !waiver.isSuccess ? (
            /* The way back to the card path — hiding it made the waiver a trap. */
            <Button variant="ghost" size="sm" className="self-start" onClick={() => setSelectedWaiver(undefined)}>
              {t('shop.deposit.waiver.clear')}
            </Button>
          ) : null}
        </CardBody>
      </Card>
      ) : null}

      {/* Card saved → the forward step as a POPUP, so the moment lands. */}
      {savedDialogOpen ? (
        <Dialog
          open={savedDialogOpen}
          onClose={() => setSavedDialogOpen(false)}
          title={t('shop.deposit.card.savedTitle')}
          size="sm"
          headerAlign="center"
          footer={
            <>
              <Button variant="secondary" onClick={() => setSavedDialogOpen(false)}>
                {t('shop.card.quickview.close')}
              </Button>
              <Button
                variant="primary"
                shape="pill"
                className="cta-sheen"
                onClick={() => navigate(toEstimate)}
              >
                {t('shop.credit.continueToEstimate')}
              </Button>
            </>
          }
        >
          <p className="text-center text-sm leading-relaxed text-foreground/70">
            {t('shop.deposit.card.savedNext')}
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}
